import { DataStatus } from '@commons/types/dataStatus';
import {
  ConsumptionDataGranularity,
  HeatingConsumptionUnit,
} from '@models/consumption';
import { DeliveryPoint } from '@models/deliveryPoint';
import { EnergyConsumptionAndCostData } from '@models/energyConsumptionAndCostFullYear';
import { EnergyConsumptionAndCostMonthsChartData } from '@models/energyConsumptionAndCostMonths';
import { EnergyConsumptionSelectedMonthsAndCostData } from '@models/energyConsumptionSelectedMonthsAndCost';
import { DisplayableForecastUnit, ForecastUnit } from '@models/forecastCost';
import { ForecastedUsageUnit } from '@models/forecastedUsage';
import { UseQueryResult } from '@tanstack/react-query';
import moment from 'moment';
import type { Moment } from 'moment';
import { v4 as uuidv4 } from 'uuid';
import type { WorkSheet } from 'xlsx';

export const filterByDate = <T extends { date: Moment }>(
  data: T[],
  startDate: Moment | undefined,
  endDate: Moment | undefined,
  granularity: moment.unitOfTime.StartOf = 'day',
) => {
  if (!startDate && !endDate) {
    return data;
  }

  return data.filter(({ date }) => {
    if (startDate && endDate) {
      return date.isBetween(startDate, endDate, granularity, '[]');
    }

    return startDate
      ? date.isSameOrAfter(startDate, granularity)
      : date.isSameOrBefore(endDate, granularity);
  });
};

export const getPlaceholderData = (
  granularity: ConsumptionDataGranularity,
  startDate: Moment,
  endDate: Moment,
  placeholderValue: number | null,
): HeatingConsumptionUnit[] => {
  const data: HeatingConsumptionUnit[] = [];
  const maxDataLength = 200;

  let date = startDate.clone();

  const granularityConverted: Exclude<
    ConsumptionDataGranularity,
    'hoursLastSevenDays' | 'hoursLastThreeMonths'
  > =
    granularity === 'hoursLastSevenDays' ||
    granularity === 'hoursLastThreeMonths'
      ? 'hours'
      : granularity;

  const amount: moment.DurationInputArg1 = {};
  amount[granularityConverted] = 1;

  data.push({
    date: moment(date),
    energy: placeholderValue,
    temperature: placeholderValue,
    fixedAndVariableFee: placeholderValue,
    energyFee: placeholderValue,
  });

  date = date.add(amount);

  while (
    date.isBetween(startDate, endDate, granularityConverted, '[]') &&
    data.length < maxDataLength
  ) {
    data.push({
      date: moment(date),
      energy: placeholderValue,
      temperature: placeholderValue,
      fixedAndVariableFee: placeholderValue,
      energyFee: placeholderValue,
    });
    date = date.add(amount);
  }

  return data;
};

export const getDataStatus = (
  requestStatus: UseQueryResult['status'],
  filteredDate: HeatingConsumptionUnit[],
): DataStatus =>
  filteredDate.length === 0 && requestStatus === 'success'
    ? 'empty'
    : requestStatus;

export const getForecastDataStatus = (
  requestStatus: UseQueryResult['status'],
  filteredDate: ForecastUnit[],
): DataStatus =>
  filteredDate.length === 0 && requestStatus === 'success'
    ? 'empty'
    : requestStatus;

export const getForecastUsageDataStatus = (
  requestStatus: UseQueryResult['status'],
  filteredDate: ForecastedUsageUnit,
): DataStatus => {
  const isEmpty =
    !filteredDate.summaryForecastedUsage.length &&
    !filteredDate.monthlyForecastedUsage.length;
  return isEmpty && requestStatus === 'success' ? 'empty' : requestStatus;
};

type EnergyConsumptionData =
  | EnergyConsumptionAndCostData
  | EnergyConsumptionAndCostMonthsChartData
  | EnergyConsumptionSelectedMonthsAndCostData;

export const getEnergyConsumptionDataStatus = (
  requestStatus: UseQueryResult['status'],
  filteredDate: EnergyConsumptionData,
): DataStatus => {
  const isEmpty =
    !filteredDate.energyConsumption.length && !filteredDate.cost.length;
  return isEmpty && requestStatus === 'success' ? 'empty' : requestStatus;
};

export const getDateFormat = (
  granularity: ConsumptionDataGranularity,
): string => {
  switch (granularity) {
    case 'hours':
    case 'hoursLastSevenDays':
    case 'hoursLastThreeMonths':
      return 'DD.MM.yyyy, HH:mm';
    case 'days':
      return 'DD.MM.yyyy';
    case 'months':
      return 'MM.yyyy';
    default: {
      const exhaustiveCheck: never = granularity;
      throw new Error(exhaustiveCheck);
    }
  }
};

interface CellsRange {
  start: {
    row: number;
    column: number;
  };
  end: {
    row: number;
    column: number;
  };
}

export const setExcelCellsNumberFormat =
  (workSheet: WorkSheet, range: CellsRange, numberFormat?: string[]) =>
  async () => {
    const xlsx = await import('xlsx');
    const defaultFormat = '0.00';
    // eslint-disable-next-line no-plusplus
    for (let { row } = range.start; row <= range.end.row; ++row) {
      for (
        let { column } = range.start;
        column <= range.end.column;
        // eslint-disable-next-line no-plusplus
        ++column
      ) {
        const cell = workSheet[xlsx.utils.encode_cell({ r: row, c: column })];
        if (cell && cell.t === 'n') {
          cell.z = numberFormat?.[column - 1] ?? defaultFormat;
        }
      }
    }
  };

interface TransformedDeliveryPoint {
  id: string;
  deliveryPointName: string;
  deliveryPointAddress: string;
  deliveryPointNumber: string;
  customerNumber: string;
}

interface TransformedData {
  id: string;
  companyName: string;
  children: TransformedDeliveryPoint[];
}

export const transformDeliveryPoint = (
  useName: string,
  point: DeliveryPoint,
): TransformedData => {
  // Helper function to handle null or empty values
  const sanitize = (value: any): string =>
    value == null || value === '' ? '' : value;

  const companyName = point.companyName || useName;

  const transformedPoint: TransformedDeliveryPoint = {
    id: sanitize(point.id),
    deliveryPointName: sanitize(point.address?.street),
    deliveryPointAddress: sanitize(point.address?.street),
    deliveryPointNumber: sanitize(point.meterNumber),
    customerNumber: sanitize(
      (point as any).customerNumber || (point as any).accountNumber || '',
    ),
  };

  return {
    id: uuidv4(),
    companyName,
    children: [transformedPoint],
  };
};

export const transformDeliveryPoints = (
  useName: string,
  data: DeliveryPoint[],
): TransformedData[] => {
  // Helper function to handle null or empty values
  const sanitize = (value: any): string =>
    value == null || value === '' ? '' : value;

  // Group delivery points by companyName, using useName if companyName is null or empty
  const groupedData: { [key: string]: TransformedDeliveryPoint[] } = {};

  data.forEach(point => {
    const companyName = point.companyName || useName;
    if (!groupedData[companyName]) {
      groupedData[companyName] = [];
    }
    groupedData[companyName].push({
      id: sanitize(point.id),
      deliveryPointName: sanitize(point.address.street),
      deliveryPointAddress: sanitize(point.address.street),
      deliveryPointNumber: sanitize(point.meterNumber),
      customerNumber: sanitize(point.customerNumber),
    });
  });

  // Map grouped data to the desired format
  return Object.keys(groupedData).map(companyName => ({
    id: uuidv4(),
    companyName,
    children: groupedData[companyName],
  }));
};

export const preprocessData = (data: DisplayableForecastUnit[]) => {
  if (!data || data.length === 0) {
    return [];
  }

  const processedData = [...data];
  return processedData;
};

export const getPreviousYearForecast = (date: Moment): Moment => {
  return date.clone().subtract(1, 'year');
};
